import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  type DisclosureProps,
  type DisclosurePanelProps,
  type DisclosureButtonProps,
} from "@headlessui/react";

import { Sidebar } from "@/components/sidebar";
import { twMerge } from "@/utils/twMerge";

import { useCreateHintHoverHandlers } from "../hints/useHintHover";

import type { HintKey } from "@/store/HintsStore";
import type { ComponentPropsWithoutRef, PropsWithChildren, ReactNode } from "react";

type BaseProps = Omit<ComponentPropsWithoutRef<"div">, "title"> & {
  title: ReactNode;
  isNested?: boolean;
  className?: string;
  buttonClassName?: string;
  action?: ReactNode;
};

export type ActionsDisclosureProps = BaseProps & {
  disclosureProps?: DisclosureProps;
  disclosureButtonProps?: DisclosureButtonProps;
  disclosurePanelProps?: DisclosurePanelProps;
  content?: () => ReactNode;
};

export function ActionsDisclosure({
  title,
  isNested,
  children,
  disclosureProps,
  disclosureButtonProps,
  disclosurePanelProps,
  content,
  className,
  buttonClassName,
  action,
  ...props
}: PropsWithChildren<ActionsDisclosureProps>) {
  const sectionButton = (open: boolean) => (
    <Sidebar.SectionButton
      as="div"
      className={twMerge("px-1.25 flex flex-1", isNested && "px-0", buttonClassName)}
      titleClassName="flex items-center"
      open={open}
    >
      {title}
    </Sidebar.SectionButton>
  );

  return (
    <Disclosure {...disclosureProps}>
      {({ open }) => (
        <Sidebar.GroupSection className={twMerge("p-0", className)} {...props}>
          {action ? (
            // The action sits beside the button: a control inside a button can't be reached
            <div className="flex w-full items-center">
              <DisclosureButton className="flex flex-1 min-w-0" {...disclosureButtonProps}>
                {sectionButton(open)}
              </DisclosureButton>
              {open && <div className="ml-auto pr-1.25">{action}</div>}
            </div>
          ) : (
            <DisclosureButton className="flex w-full" {...disclosureButtonProps}>
              {sectionButton(open)}
            </DisclosureButton>
          )}
          {open && (
            <DisclosurePanel {...disclosurePanelProps}>
              <div className={twMerge("p-1.25 pt-0", isNested && "p-0")}>
                {content?.() ?? children}
              </div>
            </DisclosurePanel>
          )}
        </Sidebar.GroupSection>
      )}
    </Disclosure>
  );
}

export type StatelessActionsDisclosureProps = BaseProps & {
  isOpen?: boolean;
  hintKey?: HintKey;
  onTriggerClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

export function StatelessActionsDisclosure({
  title,
  isNested,
  isOpen,
  className,
  buttonClassName,
  onTriggerClick,
  children,
  action,
  hintKey,
  ...props
}: PropsWithChildren<StatelessActionsDisclosureProps>) {
  const createHintHandlers = useCreateHintHoverHandlers();

  return (
    <Sidebar.GroupSection className={twMerge("p-0", className)} {...props}>
      {action ? (
        <div className="flex w-full items-center">
          <Sidebar.SectionButton
            as="button"
            type="button"
            className={twMerge("px-1.25 flex flex-1 min-w-0", isNested && "px-0", buttonClassName)}
            wrapperClassName="py-1"
            onClick={onTriggerClick}
            open={isOpen}
            aria-expanded={isOpen}
            titleClassName="flex items-center"
            {...(hintKey ? createHintHandlers(hintKey) : {})}
          >
            {title}
          </Sidebar.SectionButton>
          {isOpen && <div className="ml-auto pr-1.25">{action}</div>}
        </div>
      ) : (
        <Sidebar.SectionButton
          as="button"
          type="button"
          className={twMerge("px-1.25 w-full flex", isNested && "px-0", buttonClassName)}
          wrapperClassName="py-1"
          onClick={onTriggerClick}
          open={isOpen}
          aria-expanded={isOpen}
          titleClassName="flex items-center"
          {...(hintKey ? createHintHandlers(hintKey) : {})}
        >
          {title}
        </Sidebar.SectionButton>
      )}
      {isOpen && <div className={twMerge("p-1.25 pt-0", isNested && "p-0")}>{children}</div>}
    </Sidebar.GroupSection>
  );
}
